import { useMemo } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useQueryGetProductivityRiskTrend } from 'api/queries/getProductivityRiskTrend.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';

export type UseProductivityRiskTrendProvider = ReturnType<typeof useProductivityRiskTrendProvider>;

interface Props {
    componentKey: string;
    widgetId?: string;
    latestVersionId?: number | undefined;
    compareVersionId?: number | undefined;
    globalFilterData: any;
    localFilters: any;
    contractId: number | null | undefined;
}

export const useProductivityRiskTrendProvider = ({
    widgetId,
    latestVersionId,
    localFilters,
    globalFilterData,
    compareVersionId,
    componentKey,
    contractId,
}: Props) => {
    const { isGenerativeAi, reportId } = usePinboardWidgetWrapperContext();
    const source = Boolean(widgetId) ? localFilters : { ...globalFilterData };

    const filters = useMemo(() => {
        return getFiltersData({
            [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
            [FilterNames.TAG_CARD_BOARD]: source?.tags
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.WBS_SEARCH]: source?.wbs,
        });
    }, [source]);

    const getProductivityRiskTrendPayload = {
        compareVersionId,
        latestVersionId,
        contractId,
        filter: {
            filters,
        },
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId, reportId),
    };

    const getProductivityRiskTrend = useQueryGetProductivityRiskTrend(getProductivityRiskTrendPayload);

    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        tagsExclude: source?.tags?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        wbs: source?.wbs,
    };

    const { pillsList: pills } = useGlobalFiltersHook({ widgetId, source: pillsData, latestVersionId });

    return {
        ui: {
            state: {
                activityCodes: source?.activityCodes,
                milestoneFragnet: source?.milestoneFragnet,
                tags: source?.tags,
                wbs: source?.wbs,
            },
            pills,
        },
        api: {
            getProductivityRiskTrend: {
                instance: getProductivityRiskTrend,
                payload: getProductivityRiskTrendPayload,
            },
        },
    };
};
